import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import type { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    boundingRoiConstraintState,
    collisionConstraintState,
    intensityThresholdConstraintState,
    type BoundingRoiConstraint,
    type CollisionConstraint,
} from '~/state/atoms/toolsAtoms';
import {
    constraintCandidates,
    insideConstraintApplies,
    insideConstraintInactive,
    outsideConstraintApplies,
    outsideConstraintInactive,
} from './collisionSelection';
import { useThresholdImageRange } from './useIntensityThreshold';

interface ModifierSwitches {
    inside: BoundingRoiConstraint;
    outside: CollisionConstraint;
    /** `available`: the Threshold can apply to the active image. */
    threshold: { enabled: boolean; available: boolean };
    /** The structures Inside and Outside can apply to. */
    candidates: readonly RoiReadModel[];
}

/** The Threshold applies: it is on, and it can apply to the active image. */
export function thresholdApplies(threshold: {
    enabled: boolean;
    available: boolean;
}): boolean {
    return threshold.enabled && threshold.available;
}

/**
 * How many drawing modifiers apply, and how many are on but apply to
 * nothing.
 */
export interface ModifierCount {
    active: number;
    inactive: number;
}

/**
 * How many drawing modifiers apply to a stroke: Inside bound to one of the
 * `candidates`, Outside protecting at least one of them, and the Threshold
 * while it can apply to the active image. `inactive` counts an Inside that
 * is on but bound to none of them, and an Outside that protects structures
 * none of which is among them, so the user is told that a modifier they
 * turned on clips nothing.
 */
export function countModifiers({
    inside,
    outside,
    threshold,
    candidates,
}: ModifierSwitches): ModifierCount {
    const count = (flags: boolean[]) => flags.filter(Boolean).length;
    return {
        active: count([
            insideConstraintApplies(inside, candidates),
            outsideConstraintApplies(outside, candidates),
            thresholdApplies(threshold),
        ]),
        inactive: count([
            insideConstraintInactive(inside, candidates),
            outsideConstraintInactive(outside, candidates),
        ]),
    };
}

/** {@link countModifiers} for the modifiers in force, on the edit target. */
export function useActiveModifierCount(): ModifierCount {
    const inside = useRecoilValue(boundingRoiConstraintState);
    const outside = useRecoilValue(collisionConstraintState);
    const { enabled } = useRecoilValue(intensityThresholdConstraintState);
    const { structureSet, roiNumber: selectedRoiNumber } =
        useActiveEditTarget();
    const { available } = useThresholdImageRange();
    const candidates = useMemo(
        () =>
            constraintCandidates(
                structureSet?.roiArray ?? [],
                selectedRoiNumber
            ),
        [structureSet, selectedRoiNumber]
    );
    return countModifiers({
        inside,
        outside,
        threshold: { enabled, available },
        candidates,
    });
}
